// Accessibility patch for the OCV in-app feedback "Email" field.
//
// The "Submit Feedback to Microsoft" dialog contains a "May we contact you about
// your feedback?" section with two radio buttons ("Yes, you may contact me" /
// "No, don't contact me") and an "Email" textbox. The Email field is mandatory
// ONLY when the user opts in via the "Yes" radio. The remotely loaded Centro SDK
// renders this field without a visual required asterisk and without
// `aria-required`, so neither sighted users nor screen-reader users can tell it
// is required when it matters (WCAG 3.3.2).
//
// Because the markup comes from a remotely loaded SDK bundle and the required
// state depends on a runtime selection, we observe the feedback container and
// keep the Email field's required indicators in sync with the radio choice:
// required (asterisk + aria-required="true") when "Yes" is selected, and not
// required otherwise.

const REQUIRED_MARKER_CLASS = 'rl-ocv-email-required';
const EMAIL_INPUT_SELECTOR = 'input[data-testid="email-input"]';
const RADIO_SELECTOR = 'input[type="radio"]';
const CONTACT_SECTION_SELECTOR = '[data-testid="radio-box-and-labeled-checkbox-section"]';
const RADIO_LISTENER_FLAG = 'rlOcvContactRadioBound';

type BoundRadio = HTMLInputElement & { [RADIO_LISTENER_FLAG]?: boolean };

let activeObserver: MutationObserver | null = null;

const getRadioLabelText = (radio: HTMLInputElement): string => {
  const labels = radio.labels ? Array.from(radio.labels) : [];
  let text = labels.map((label) => label.textContent ?? '').join(' ');

  // Fall back to a sibling Fluent UI radio label when `labels` is unavailable.
  if (!text.trim()) {
    const sibling = radio.parentElement?.querySelector('label, .fui-Radio__label');
    text = sibling?.textContent ?? '';
  }
  return text.trim().toLowerCase();
};

const getScopeForInput = (input: HTMLInputElement, fallbackScope: ParentNode): ParentNode => {
  return (input.closest(CONTACT_SECTION_SELECTOR) as ParentNode | null) ?? fallbackScope;
};

// Determines whether the user has opted in to being contacted. Returns true only
// when the affirmative ("Yes, you may contact me") radio is selected.
const isContactOptedIn = (scope: ParentNode): boolean => {
  const radios = Array.from(scope.querySelectorAll<HTMLInputElement>(RADIO_SELECTOR));
  if (radios.length === 0) {
    // No contact radios present: the Email field only renders once contact is
    // requested, so treat its presence as opted in.
    return true;
  }

  const affirmative = radios.find((radio) => {
    const text = getRadioLabelText(radio);
    return (
      text.includes('contact') && !text.includes("don't") && !text.includes('do not') && !text.includes('not contact')
    );
  });

  return affirmative ? affirmative.checked : radios.some((radio) => radio.checked);
};

const setEmailRequired = (input: HTMLInputElement, scope: ParentNode, required: boolean): void => {
  input.setAttribute('aria-required', required ? 'true' : 'false');

  const field = input.closest('.fui-Field') ?? scope.querySelector('[data-testid="email-collection-in-form"]');
  const label = field?.querySelector<HTMLLabelElement>('label') ?? null;
  if (!label) {
    return;
  }

  const existing = label.querySelector(`.${REQUIRED_MARKER_CLASS}`);
  if (required && !existing) {
    const asterisk = document.createElement('span');
    // `fui-Label__required` matches the Fluent UI v9 styling used elsewhere in the form.
    asterisk.className = `fui-Label__required ${REQUIRED_MARKER_CLASS}`;
    asterisk.setAttribute('aria-hidden', 'true');
    asterisk.textContent = ' *';
    label.appendChild(asterisk);
  } else if (!required && existing) {
    existing.remove();
  }
};

const syncEmailRequiredState = (container: HTMLElement): void => {
  const searchRoot: ParentNode = container.ownerDocument ?? container;
  const input = searchRoot.querySelector<HTMLInputElement>(EMAIL_INPUT_SELECTOR);
  if (!input) {
    return;
  }

  const scope = getScopeForInput(input, searchRoot);
  bindRadioListeners(scope, container);
  setEmailRequired(input, scope, isContactOptedIn(scope));
};

// Re-evaluates the required state whenever a contact radio changes.
const bindRadioListeners = (scope: ParentNode, container: HTMLElement): void => {
  const radios = scope.querySelectorAll<BoundRadio>(RADIO_SELECTOR);
  radios.forEach((radio) => {
    if (radio[RADIO_LISTENER_FLAG]) {
      return;
    }
    radio[RADIO_LISTENER_FLAG] = true;
    radio.addEventListener('change', () => syncEmailRequiredState(container));
  });
};

/**
 * Keeps the feedback "Email" field's required indicators (visual asterisk and
 * `aria-required`) in sync with the "May we contact you about your feedback?"
 * radio selection. The field is marked required only when the user selects
 * "Yes, you may contact me". Safe to call on every feedback launch; only one
 * observer is kept active at a time.
 */
export const enforceFeedbackEmailRequired = (container: HTMLElement): void => {
  activeObserver?.disconnect();

  // Handle anything already rendered, then keep watching for the SDK to render
  // or re-render the contact section / email field.
  syncEmailRequiredState(container);

  activeObserver = new MutationObserver(() => {
    syncEmailRequiredState(container);
  });
  const observationRoot: Node = container.ownerDocument?.body ?? container;
  activeObserver.observe(observationRoot, { childList: true, subtree: true });
};

/** Stops observing the feedback container. Exposed primarily for testing/cleanup. */
export const stopEnforcingFeedbackEmailRequired = (): void => {
  activeObserver?.disconnect();
  activeObserver = null;
};
